(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  var LS_ENABLED = 'translationModeEnabled';
  var LS_LANG = 'translationTargetLang';
  var MAX_TEXT_CHARS = 4000;
  var MAX_CONCURRENT = 2;
  var CIRCUIT_FAILS = 2;
  var CIRCUIT_COOLDOWN_MS = 45000;
  var PINYIN_PRO_URL = 'https://unpkg.com/pinyin-pro@3.29.1/dist/index.js';
  var GLOSSARY_SCRIPT_PATH = '/js/translation-glossary.js';
  var translationCache = new Map();
  var readingLibraryPromises = {};
  var glossaryClientPromise = null;
  var observer = null;
  var activeTranslate = 0;
  var translateQueue = [];
  var consecutiveFails = 0;
  var circuitOpenUntil = 0;

  var TRANSLATION_LANGS = {
    zh: { name: 'Chinese (Mandarin + Pinyin)', speech: 'zh-CN', reading: true },
    ja: { name: 'Japanese (Romaji)', speech: 'ja-JP', reading: true },
    ko: { name: 'Korean (Romanized)', speech: 'ko-KR', reading: true },
    es: { name: 'Spanish', speech: 'es-ES', reading: false },
    fr: { name: 'French', speech: 'fr-FR', reading: false },
    de: { name: 'German', speech: 'de-DE', reading: false },
    it: { name: 'Italian', speech: 'it-IT', reading: false },
    pt: { name: 'Portuguese', speech: 'pt-BR', reading: false },
    ru: { name: 'Russian', speech: 'ru-RU', reading: false },
    hi: { name: 'Hindi', speech: 'hi-IN', reading: true },
    ar: { name: 'Arabic', speech: 'ar-SA', reading: true },
    vi: { name: 'Vietnamese', speech: 'vi-VN', reading: false },
    th: { name: 'Thai', speech: 'th-TH', reading: true },
    id: { name: 'Indonesian', speech: 'id-ID', reading: false },
    tr: { name: 'Turkish', speech: 'tr-TR', reading: false },
    nl: { name: 'Dutch', speech: 'nl-NL', reading: false },
    pl: { name: 'Polish', speech: 'pl-PL', reading: false },
    en: { name: 'English', speech: 'en-US', reading: false },
  };

  function isEnabled() {
    try {
      return localStorage.getItem(LS_ENABLED) === 'true';
    } catch (error) {
      return false;
    }
  }

  function getTargetLang() {
    try {
      var lang = localStorage.getItem(LS_LANG) || 'zh';
      return TRANSLATION_LANGS[lang] ? lang : 'zh';
    } catch (error) {
      return 'zh';
    }
  }

  function speechTagFor(lang) {
    var info = TRANSLATION_LANGS[lang];
    return info ? info.speech : lang;
  }

  function staticBase() {
    return String(window.STATIC_URL || '/static').replace(/\/$/, '');
  }

  function ensureGlossaryClient() {
    if (window.translationGlossary) {
      window.translationGlossary.attach();
      return Promise.resolve(window.translationGlossary);
    }
    if (glossaryClientPromise) {
      return glossaryClientPromise;
    }
    glossaryClientPromise = new Promise(function (resolve) {
      var script = document.createElement('script');
      script.src = staticBase() + GLOSSARY_SCRIPT_PATH;
      script.async = true;
      script.dataset.translationGlossary = 'true';
      script.onload = function () {
        var client = window.translationGlossary || null;
        if (client) {
          client.attach();
        }
        resolve(client);
      };
      script.onerror = function () {
        resolve(null);
      };
      (document.head || document.documentElement).appendChild(script);
    });
    return glossaryClientPromise;
  }

  function ensureReadingLibrary(lang) {
    if (lang !== 'zh' || (window.pinyinPro && typeof window.pinyinPro.pinyin === 'function')) {
      return Promise.resolve();
    }
    if (readingLibraryPromises[lang]) {
      return readingLibraryPromises[lang];
    }
    readingLibraryPromises[lang] = new Promise(function (resolve) {
      var script = document.createElement('script');
      script.src = PINYIN_PRO_URL;
      script.async = true;
      script.dataset.translationReading = lang;
      script.onload = function () {
        resolve();
      };
      script.onerror = function () {
        resolve();
      };
      (document.head || document.documentElement).appendChild(script);
    });
    return readingLibraryPromises[lang];
  }

  function injectStyles() {
    if (document.getElementById('translation-mode-styles')) {
      return;
    }
    var style = document.createElement('style');
    style.id = 'translation-mode-styles';
    style.textContent = [
      '.translation-block{margin:6px 0 2px;padding:8px 10px;border-left:3px solid #d6863f;background:rgba(214,134,63,0.07);border-radius:0 8px 8px 0;font-size:0.95em;}',
      '.translation-block--loading{opacity:0.6;font-style:italic;}',
      '.translation-block--error{border-left-color:#b3433d;opacity:0.7;}',
      '.translation-line{margin:4px 0;}',
      '.translation-line+.translation-line{border-top:1px dashed rgba(128,128,128,0.25);padding-top:6px;}',
      '.translation-line__src{opacity:0.65;font-size:0.88em;margin-bottom:2px;}',
      '.translation-line__reading{opacity:0.78;font-size:0.84em;line-height:1.35;margin:2px 0 1px;letter-spacing:0.02em;}',
      '.translation-line__dst{display:flex;flex-wrap:wrap;align-items:center;gap:2px;line-height:1.6;}',
      '.translation-word{cursor:pointer;border-radius:4px;padding:0 3px;transition:background 0.15s;user-select:none;}',
      '.translation-word:hover,.translation-word:active{background:rgba(214,134,63,0.25);}',
      '.translation-line__play{background:none;border:none;cursor:pointer;padding:1px;vertical-align:middle;opacity:0.55;margin-left:4px;}',
      '.translation-line__play:hover{opacity:1;}',
      '.translation-line__play .material-icons{font-size:17px;}',
    ].join('\n');
    document.head.appendChild(style);
  }

  function circuitOpen() {
    return Date.now() < circuitOpenUntil;
  }

  function noteTranslateSuccess() {
    consecutiveFails = 0;
    circuitOpenUntil = 0;
  }

  function noteTranslateFailure() {
    consecutiveFails += 1;
    if (consecutiveFails >= CIRCUIT_FAILS) {
      circuitOpenUntil = Date.now() + CIRCUIT_COOLDOWN_MS;
    }
  }

  function pumpTranslateQueue() {
    while (activeTranslate < MAX_CONCURRENT && translateQueue.length) {
      var job = translateQueue.shift();
      activeTranslate += 1;
      job()
        .catch(function () {})
        .then(function () {
          activeTranslate -= 1;
          pumpTranslateQueue();
        });
    }
  }

  function enqueueTranslate(run) {
    return new Promise(function (resolve, reject) {
      translateQueue.push(function () {
        return run().then(resolve, reject);
      });
      pumpTranslateQueue();
    });
  }

  function translateText(text, lang) {
    var key = lang + '|' + text;
    if (translationCache.has(key)) {
      return translationCache.get(key);
    }
    if (circuitOpen()) {
      return Promise.reject(new Error('translate cooling down'));
    }
    var promise = enqueueTranslate(function () {
      if (circuitOpen()) {
        return Promise.reject(new Error('translate cooling down'));
      }
      return fetch('/api/translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: text, target_lang: lang }),
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('translate failed: ' + response.status);
          }
          return response.json();
        })
        .then(function (data) {
          if (!data || !data.success || !data.data) {
            throw new Error((data && data.error) || 'translate failed');
          }
          noteTranslateSuccess();
          return data.data;
        });
    });
    translationCache.set(key, promise);
    promise.catch(function () {
      translationCache.delete(key);
      noteTranslateFailure();
    });
    return promise;
  }

  function buildWordSpan(word, lang) {
    var span = document.createElement('span');
    span.className = 'translation-word';
    span.setAttribute('role', 'button');
    span.tabIndex = 0;
    span.textContent = word.w;
    span.dataset.speakWord = word.w;
    span.dataset.speakLang = speechTagFor(lang);
    span.dataset.glossaryWord = word.w;
    span.dataset.glossaryLang = lang;
    var glossaryEntry = window.translationGlossary && typeof window.translationGlossary.lookup === 'function'
      ? window.translationGlossary.lookup(lang, word.w)
      : null;
    var meaning = word.m || (glossaryEntry && glossaryEntry.g && glossaryEntry.g.length ? glossaryEntry.g[0] : '');
    if (meaning) {
      span.title = meaning;
    }
    return span;
  }

  function appendGlossarySegments(element, text, lang) {
    var glossary = window.translationGlossary;
    if (!glossary || typeof glossary.segment !== 'function') {
      return false;
    }
    var segments = glossary.segment(lang, text);
    if (!segments || !segments.length) {
      return false;
    }
    segments.forEach(function (segment) {
      if (segment.known) {
        var gloss = segment.entry && segment.entry.g && segment.entry.g.length ? segment.entry.g[0] : '';
        element.appendChild(buildWordSpan({ w: segment.text, m: gloss }, lang));
      } else {
        element.appendChild(document.createTextNode(segment.text));
      }
    });
    return true;
  }

  function buildPlayButton(text, langTag) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'translation-line__play';
    button.setAttribute('aria-label', 'Play line');
    button.dataset.speakLine = text;
    button.dataset.speakLang = langTag;
    var icon = document.createElement('span');
    icon.className = 'material-icons';
    icon.textContent = 'volume_up';
    button.appendChild(icon);
    return button;
  }

  function readingForLine(line, lang) {
    if (!line || !line.dst) {
      return '';
    }
    if (lang === 'zh' && window.pinyinPro && typeof window.pinyinPro.pinyin === 'function') {
      try {
        return window.pinyinPro.pinyin(line.dst, { toneType: 'symbol', nonZh: 'spaced' })
          .replace(/\s+([，。！？；：、,.!?;:)）】」』》])/g, '$1')
          .replace(/([，。！？；：、,.!?;:）】」』》])\s+/g, '$1')
          .replace(/([（(【「『《])\s+/g, '$1');
      } catch {}
    }
    if (line.words && line.words.length) {
      return line.words
        .map(function (word) {
          return word && word.r ? String(word.r).trim() : '';
        })
        .filter(Boolean)
        .join(' ');
    }
    return '';
  }

  function renderTranslation(block, data, lang) {
    block.classList.remove('translation-block--loading');
    block.textContent = '';
    var lines = (data && data.lines) || [];
    if (!lines.length) {
      block.remove();
      return;
    }
    lines.forEach(function (line) {
      if (!line || (!line.dst && !line.src)) {
        return;
      }
      var lineEl = document.createElement('div');
      lineEl.className = 'translation-line';
      if (line.src && line.src !== line.dst) {
        var srcEl = document.createElement('div');
        srcEl.className = 'translation-line__src';
        srcEl.textContent = line.src;
        lineEl.appendChild(srcEl);
      }
      var reading = readingForLine(line, lang);
      if (reading) {
        var readingEl = document.createElement('div');
        readingEl.className = 'translation-line__reading';
        readingEl.textContent = reading;
        lineEl.appendChild(readingEl);
      }
      var dstEl = document.createElement('div');
      dstEl.className = 'translation-line__dst';
      if (line.words && line.words.length) {
        line.words.forEach(function (word) {
          if (word && word.w) {
            dstEl.appendChild(buildWordSpan(word, lang));
          }
        });
      } else if (line.dst) {
        if (!appendGlossarySegments(dstEl, line.dst, lang)) {
          dstEl.textContent = line.dst;
        }
      }
      if (line.dst) {
        dstEl.appendChild(buildPlayButton(line.dst, speechTagFor(lang)));
      }
      lineEl.appendChild(dstEl);
      block.appendChild(lineEl);
    });
  }

  function processChatItem(item) {
    if (!isEnabled() || !item || !item.querySelector) {
      return;
    }
    if (item.classList.contains('chat-log__item--own')) {
      return;
    }
    if (item.querySelector('.translation-block')) {
      return;
    }
    var messageEl = item.querySelector('.chat-log__message');
    if (!messageEl) {
      return;
    }
    var text = (messageEl.textContent || '').trim();
    if (!text || text.length > MAX_TEXT_CHARS) {
      return;
    }
    var lang = getTargetLang();
    var block = document.createElement('div');
    block.className = 'translation-block translation-block--loading';
    block.textContent = 'translating…';
    messageEl.insertAdjacentElement('afterend', block);
    Promise.all([translateText(text, lang), ensureReadingLibrary(lang), ensureGlossaryClient()])
      .then(function (results) {
        var data = results[0];
        var glossary = results[2];
        var glossaryReady = glossary ? glossary.prepare(lang, data.lines).catch(function () { return null; }) : Promise.resolve();
        return glossaryReady.then(function () {
          renderTranslation(block, data, lang);
        });
      })
      .catch(function (error) {
        var message = error && error.message ? String(error.message) : '';
        if (message.indexOf('cooling down') === -1) {
          console.warn('translation failed:', error);
        }
        block.classList.add('translation-block--error');
        block.textContent = circuitOpen() ? 'translation paused' : 'translation unavailable';
        setTimeout(function () {
          block.remove();
        }, circuitOpen() ? 2500 : 4000);
      });
  }

  function processExistingMessages() {
    if (circuitOpen()) {
      return;
    }
    var items = document.querySelectorAll('.chat-log__item');
    var start = Math.max(0, items.length - 6);
    for (var i = start; i < items.length; i++) {
      processChatItem(items[i]);
    }
  }

  function clearTranslations() {
    document.querySelectorAll('.translation-block').forEach(function (block) {
      block.remove();
    });
  }

  function ensureObserver() {
    if (observer || typeof MutationObserver === 'undefined' || !document.body) {
      return;
    }
    var target = document.querySelector('.chat-log') || document.body;
    observer = new MutationObserver(function (mutations) {
      if (!isEnabled()) {
        return;
      }
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (!node || node.nodeType !== 1) {
            return;
          }
          if (node.classList && node.classList.contains('chat-log__item')) {
            processChatItem(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll('.chat-log__item').forEach(processChatItem);
          }
        });
      });
    });
    observer.observe(target, { childList: true, subtree: true });
  }

  function setTranslationMode(enabled, lang) {
    try {
      localStorage.setItem(LS_ENABLED, enabled ? 'true' : 'false');
      if (lang && TRANSLATION_LANGS[lang]) {
        localStorage.setItem(LS_LANG, lang);
      }
    } catch (error) {}
    if (enabled) {
      injectStyles();
      ensureObserver();
      ensureReadingLibrary(lang || getTargetLang());
      ensureGlossaryClient().then(function (client) {
        if (client) {
          return client.loadLanguage(lang || getTargetLang());
        }
        return null;
      }).catch(function () {});
      clearTranslations();
      processExistingMessages();
    } else {
      clearTranslations();
    }
  }

  document.addEventListener('click', function (event) {
    var target = event.target && event.target.closest
      ? event.target.closest('[data-speak-word],[data-speak-line]')
      : null;
    if (!target) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    var text = target.dataset.speakWord || target.dataset.speakLine;
    var lang = target.dataset.speakLang || 'en-US';
    var rate = target.dataset.speakWord ? 0.75 : 0.9;
    if (typeof window.speakTextInLanguage === 'function') {
      window.speakTextInLanguage(text, lang, rate);
    }
  }, true);

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    var target = event.target;
    if (target && target.dataset && target.dataset.speakWord) {
      event.preventDefault();
      if (typeof window.speakTextInLanguage === 'function') {
        window.speakTextInLanguage(target.dataset.speakWord, target.dataset.speakLang || 'en-US', 0.75);
      }
    }
  });

  function bindSettingsControls() {
    var toggle = document.getElementById('translation-mode-switch');
    var langSelect = document.getElementById('translation-lang-select');
    if (!toggle || !langSelect) {
      return;
    }
    if (!langSelect.options.length) {
      Object.keys(TRANSLATION_LANGS).forEach(function (code) {
        var option = document.createElement('option');
        option.value = code;
        option.textContent = TRANSLATION_LANGS[code].name;
        langSelect.appendChild(option);
      });
    }
    toggle.checked = isEnabled();
    langSelect.value = getTargetLang();
    var wrap = toggle.closest('.mdl-switch');
    if (wrap) {
      wrap.classList.toggle('is-checked', toggle.checked);
    }
    function apply() {
      setTranslationMode(toggle.checked, langSelect.value);
    }
    toggle.addEventListener('change', apply);
    langSelect.addEventListener('change', apply);
  }

  function init() {
    bindSettingsControls();
    if (!document.querySelector('.chat-log')) {
      return;
    }
    if (isEnabled()) {
      injectStyles();
      ensureObserver();
      ensureReadingLibrary(getTargetLang());
      ensureGlossaryClient().then(function (client) {
        if (client) {
          return client.loadLanguage(getTargetLang());
        }
        return null;
      }).catch(function () {});
      processExistingMessages();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.translationMode = {
    isEnabled: isEnabled,
    getTargetLang: getTargetLang,
    setMode: setTranslationMode,
    languages: TRANSLATION_LANGS,
    refresh: processExistingMessages,
    translateText: translateText,
    circuitOpen: circuitOpen,
    _test: {
      noteTranslateFailure: noteTranslateFailure,
      noteTranslateSuccess: noteTranslateSuccess,
      resetCircuit: function () {
        consecutiveFails = 0;
        circuitOpenUntil = 0;
        translateQueue = [];
        activeTranslate = 0;
        translationCache.clear();
        readingLibraryPromises = {};
        glossaryClientPromise = null;
      },
      renderTranslation: renderTranslation,
    },
  };
})();
